import { createContext, useContext } from "react";
import { authStorage } from "@features/auth/lib/auth-storage";

export type AppearanceMode = "light" | "dark";
const cacheKey = (userId: number | string) => `orbit-appearance:${userId}`;
export const appearanceSettingsKey = (userId?: number | string) =>
  ["profile", "settings", userId] as const;

export function readAppearance(userId?: number | string): AppearanceMode {
  try {
    return userId != null && localStorage.getItem(cacheKey(userId)) === "dark"
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
}

export function cacheAppearance(userId: number | string, mode: AppearanceMode) {
  try {
    localStorage.setItem(cacheKey(userId), mode);
  } catch {
    /* Storage can be unavailable in private browsing. */
  }
}

/** Apply the signed-in account's cached mode before React paints. */
export function initializeAppearance() {
  let userId: number | string | undefined;
  try {
    userId = authStorage.get()?.user.id;
  } catch {
    /* Default to light without browser storage. */
  }
  const mode = readAppearance(userId);
  document.documentElement.dataset.orbitTheme = mode;
  document.documentElement.style.colorScheme = mode;
  document.documentElement.style.backgroundColor =
    mode === "dark" ? "#08121c" : "#F5F7FB";
}

interface AppearanceContextValue {
  mode: AppearanceMode;
  setMode(mode: AppearanceMode): void;
  saving: boolean;
  loading: boolean;
  error: string | null;
}
export const AppearanceContext = createContext<AppearanceContextValue | null>(
  null,
);

export function useAppearance() {
  const value = useContext(AppearanceContext);
  if (!value)
    throw new Error("useAppearance must be used within AppearanceProvider");
  return value;
}
